import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLogin } from '../api/hooks';
import { Button, Card, ErrorText, Field, TextInput } from '../components/ui';
import { zh } from '../i18n';

/**
 * 登入 (SPEC §10). The server returns one identical message for unknown email
 * and wrong password (CONTRACTS §2), so this screen never elaborates on which
 * half failed — it just shows what came back.
 */
export function LoginRoute(): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const login = useLogin();
  const navigate = useNavigate();

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!email.trim() || !password) {
      setLocalError(zh.login.missingFields);
      return;
    }
    setLocalError(null);
    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (result) => {
          navigate(result.mustChangePassword ? '/first-run' : '/', { replace: true });
        },
      },
    );
  };

  return (
    <div className="auth">
      <Card className="auth__card">
        <h1 className="auth__title">{zh.login.title}</h1>
        <p className="small muted">{zh.login.subtitle}</p>
        <form className="stack" onSubmit={onSubmit}>
          <Field label={zh.login.email}>
            {(id) => (
              <TextInput
                id={id}
                type="email"
                autoComplete="username"
                inputMode="email"
                placeholder={zh.login.emailPlaceholder}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            )}
          </Field>
          <Field label={zh.login.password} error={localError}>
            {(id) => (
              <TextInput
                id={id}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            )}
          </Field>
          <ErrorText error={login.error} />
          <Button variant="primary" block type="submit" disabled={login.isPending}>
            {login.isPending ? zh.login.submitting : zh.login.submit}
          </Button>
        </form>
      </Card>
    </div>
  );
}
